import { type AxialCoord, hexDistance, hexKey } from "../hex";
import { hexNoise, MAP_CENTRE } from "../map";
import { rankBy } from "../pick";
import { nextInt, nextUint32, type RngState } from "../rng";
import {
  imagesOf,
  inSector,
  orbitOf,
  type SymmetryName,
  symmetryOrder,
  symmetryStep,
} from "../symmetry";
import type { RunRules, StartRules } from "../types/rules";
import type { StructureKind } from "../types/state";
import {
  BEST_CANDIDATE_SAMPLES,
  PLACEMENT_TRIES,
  STRUCTURE_MIN_SPACING,
  type Starts,
} from "./placement";

/**
 * K5's procgen (ADR-0017 point 5, docs/design/KINGDOM-SEATS-IDEA.md section 7.1): the starts and
 * the land of a map grown in a symmetry group's orbits (`rules.starts`), so every side is an exact
 * image of the first and slot i of one side meets the same land as slot i of any other. Pure over
 * coords, the rng and the rules, like `placement.ts`, which keeps the duel's half turn untouched.
 */

/**
 * Each start's share of the hexes nearer it than any other start, a hex at equal distance split
 * evenly among those it ties. Counted by tie size before it is summed, so two starts that are
 * images of each other get the same number exactly.
 */
export function nearestLandShares(
  hexes: readonly AxialCoord[],
  starts: readonly AxialCoord[],
): number[] {
  const byTie = starts.map(() => starts.map(() => 0));
  for (const hex of hexes) {
    let nearest = Number.POSITIVE_INFINITY;
    let tied: number[] = [];
    starts.forEach((start, seat) => {
      const distance = hexDistance(start, hex);
      if (distance < nearest) {
        nearest = distance;
        tied = [seat];
      } else if (distance === nearest) {
        tied.push(seat);
      }
    });
    for (const seat of tied) {
      const counts = byTie[seat];
      if (counts) {
        counts[tied.length - 1] = (counts[tied.length - 1] ?? 0) + 1;
      }
    }
  }
  return byTie.map(
    (counts) =>
      counts.reduce((share, count, index) => share + count / (index + 1), 0) / hexes.length,
  );
}

/** Every side's starts from side 1's, side-major: side j's slot i is side 1's slot i under element j. */
function sidesOf(side: readonly AxialCoord[], symmetry: SymmetryName): AxialCoord[] {
  const images = side.map((start) => imagesOf(start, symmetry));
  const seats: AxialCoord[] = [];
  for (let element = 0; element < symmetryOrder(symmetry); element += 1) {
    for (const slot of images) {
      const image = slot[element];
      if (image) {
        seats.push(image);
      }
    }
  }
  return seats;
}

/**
 * The starts of a run with `rules.starts`. Side 1's kingdoms are drawn in its sector among the hexes
 * no element of the group fixes, and among those at least `minStartDistance` from their own images
 * (the duel's rule for its twins) while the sector holds a side's worth of them. Each kingdom is
 * drawn best-candidate, the one of `BEST_CANDIDATE_SAMPLES` draws farthest from every start already
 * placed and from its own images, among the hexes `cityMinSpacing` from all of them while any is
 * (a capital is a city). The other sides are their images. The side is kept when its largest
 * nearest-land share is within `spreadPercent` of its smallest (always, with one kingdom a side),
 * else drawn again, `draws` times at most; when none is, the least spread drawn, the first on a tie.
 */
export function pickSymmetricStarts(
  hexes: readonly AxialCoord[],
  rng: RngState,
  rules: RunRules,
  factionCount: number,
  starts: StartRules,
): Starts {
  const { symmetry } = starts;
  const order = symmetryOrder(symmetry);
  if (factionCount % order !== 0) {
    throw new RangeError(
      `${factionCount} kingdoms do not divide among the ${order} sides of ${symmetry}`,
    );
  }
  const perSide = factionCount / order;
  const sector = hexes.filter(
    (coord) => inSector(coord, symmetry) && orbitOf(coord, symmetry).length === order,
  );
  // How near each hex's own images are: they are the other sides' kingdoms in its slot.
  const twinGap = (coord: AxialCoord): number =>
    Math.min(
      ...imagesOf(coord, symmetry)
        .slice(1)
        .map((twin) => hexDistance(twin, coord)),
    );
  const roomy = sector.filter((coord) => twinGap(coord) >= rules.minStartDistance);
  const candidates = roomy.length >= perSide ? roomy : sector;
  const ownGaps = candidates.map(twinGap);
  let state = rng;
  const drawSide = (): AxialCoord[] => {
    const side: AxialCoord[] = [];
    // Each candidate's distance to the nearest start placed, its images included.
    const placedGaps = candidates.map(() => Number.POSITIVE_INFINITY);
    const gapOf = (index: number): number => Math.min(ownGaps[index] ?? 0, placedGaps[index] ?? 0);
    for (let slot = 0; slot < perSide; slot += 1) {
      const free = candidates.flatMap((_, index) => ((placedGaps[index] ?? 0) > 0 ? [index] : []));
      const spaced = free.filter((index) => gapOf(index) >= rules.cityMinSpacing);
      const pool = spaced.length > 0 ? spaced : free;
      let best: AxialCoord | undefined;
      let bestSpread = -1;
      for (let sample = 0; sample < BEST_CANDIDATE_SAMPLES && pool.length > 0; sample += 1) {
        const pick = nextInt(state, pool.length);
        state = pick.state;
        const index = pool[pick.value];
        const coord = index === undefined ? undefined : candidates[index];
        if (index === undefined || !coord) {
          continue;
        }
        const spread = gapOf(index);
        if (spread > bestSpread) {
          best = coord;
          bestSpread = spread;
        }
      }
      if (!best) {
        throw new RangeError(`the map has no room for ${factionCount} kingdoms`);
      }
      side.push(best);
      for (const image of imagesOf(best, symmetry)) {
        candidates.forEach((coord, index) => {
          placedGaps[index] = Math.min(placedGaps[index] ?? 0, hexDistance(image, coord));
        });
      }
    }
    return side;
  };
  let kept: { readonly seats: AxialCoord[]; readonly spread: number } | undefined;
  for (let draw = 0; draw < Math.max(1, starts.draws); draw += 1) {
    const seats = sidesOf(drawSide(), symmetry);
    const shares = nearestLandShares(hexes, seats).slice(0, perSide);
    const largest = Math.max(...shares);
    const smallest = Math.min(...shares);
    if (kept === undefined || largest / smallest < kept.spread) {
      kept = { seats, spread: largest / smallest };
    }
    if (largest * 100 <= smallest * starts.spreadPercent) {
      break;
    }
  }
  return { seats: kept?.seats ?? [], rng: state };
}

/**
 * `placeLand` for a map grown in orbits: rough ground and structures come in whole orbits of the
 * group, so every side is handed the same land. Nothing lands on a start or next to one. Rough
 * ground takes the orbits of the hexes where a seeded noise field is highest while the orbit fits
 * the count (so it can end short by less than the group's order). When the structure count is not
 * a multiple of the order, the centre, the one hex every side shares, takes one; then orbits of
 * cities and of wells alternate, each placed best-candidate (`BEST_CANDIDATE_SAMPLES` draws, the
 * one farthest from every start and structure wins), its images `STRUCTURE_MIN_SPACING` apart.
 */
export function placeSymmetricLand(
  coords: readonly AxialCoord[],
  starts: Starts,
  rules: RunRules,
  noiseScale: number,
  symmetry: SymmetryName,
): { rough: Set<string>; structures: Map<string, StructureKind>; rng: RngState } {
  const index = new Set(coords.map(hexKey));
  const anchors = starts.seats;
  const forbidden = new Set(
    coords.filter((coord) => anchors.some((start) => hexDistance(coord, start) <= 1)).map(hexKey),
  );
  const allowed = (coord: AxialCoord): boolean => {
    const key = hexKey(coord);
    return index.has(key) && !forbidden.has(key);
  };
  let state = starts.rng;

  const saltStep = nextUint32(state);
  state = saltStep.state;
  const ranked = rankBy(
    coords.filter(allowed),
    (coord) => -hexNoise(coord, noiseScale, saltStep.value),
  );
  const rough = new Set<string>();
  for (const coord of ranked) {
    if (rough.size >= rules.roughHexCount) {
      break;
    }
    const orbit = orbitOf(coord, symmetry);
    if (
      !rough.has(hexKey(coord)) &&
      rough.size + orbit.length <= rules.roughHexCount &&
      orbit.every(allowed)
    ) {
      for (const image of orbit) {
        rough.add(hexKey(image));
      }
    }
  }

  const structures = new Map<string, StructureKind>();
  const placed: AxialCoord[] = [];
  const order = symmetryOrder(symmetry);
  const step = symmetryStep(symmetry);
  const kindFor = (orbitIndex: number): StructureKind => (orbitIndex % 2 === 0 ? "city" : "well");
  const site = (coord: AxialCoord): boolean =>
    allowed(coord) &&
    !structures.has(hexKey(coord)) &&
    placed.every((placedCoord) => hexDistance(placedCoord, coord) >= STRUCTURE_MIN_SPACING);
  const apart = (orbit: readonly AxialCoord[]): boolean =>
    orbit.every((image, at) =>
      orbit.slice(at + 1).every((other) => hexDistance(image, other) >= STRUCTURE_MIN_SPACING),
    );
  if (rules.structureCount % order !== 0 && site(MAP_CENTRE)) {
    structures.set(hexKey(MAP_CENTRE), kindFor(Math.floor(rules.structureCount / order)));
    placed.push(MAP_CENTRE);
  }
  let orbitIndex = 0;
  for (
    let tries = 0;
    structures.size + step <= rules.structureCount && tries < PLACEMENT_TRIES;
    tries += 1
  ) {
    let best: AxialCoord[] | undefined;
    let bestSpread = -1;
    for (let sample = 0; sample < BEST_CANDIDATE_SAMPLES; sample += 1) {
      const pick = nextInt(state, coords.length);
      state = pick.state;
      const coord = coords[pick.value];
      if (!coord) {
        continue;
      }
      const orbit = orbitOf(coord, symmetry);
      if (
        orbit.length < step ||
        structures.size + orbit.length > rules.structureCount ||
        !orbit.every(site) ||
        !apart(orbit)
      ) {
        continue;
      }
      const spread = Math.min(
        ...[...anchors, ...placed].map((placedCoord) => hexDistance(placedCoord, coord)),
      );
      if (spread > bestSpread) {
        best = orbit;
        bestSpread = spread;
      }
    }
    if (best) {
      const kind = kindFor(orbitIndex);
      orbitIndex += 1;
      for (const coord of best) {
        structures.set(hexKey(coord), kind);
        placed.push(coord);
      }
    }
  }
  return { rough, structures, rng: state };
}
